import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormGroup,
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Config as NgOtpInputConfig } from 'ng-otp-input/lib/models/config';
import { NgOtpInputModule } from 'ng-otp-input';
import {
  AutoRegistrationService,
  ComponentBase,
  SharedService,
  ErrorFormComponent,
  NumberOnlyDirective,
  VerifyMobileNumberType,
} from 'src/app/core';
import { takeUntil, finalize } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { CONFIG } from '../../registration-config';
import { AutoRegistrationConfig } from '../../models/auto-registration-config';
import { VerifyIdentityComponent } from '../../sign-up/verification/verify-identity/verify-identity.component';
import { AutoRegistrationTopBarComponent } from 'src/app/shared/auto-registration-top-bar/auto-registration-top-bar.component';
import { MatDialog } from '@angular/material/dialog';
import { VideoComponent } from 'src/app/shared/video/video.component';

type ResetPasswordStep = 'enter_phone' | 'enter_otp' | 'enter_new_password';

@Component({
  selector: 'app-reset-password2',
  standalone: true,
  imports: [
      CommonModule,
      ReactiveFormsModule,
      FlexLayoutModule,
      MatFormFieldModule,
      MatInputModule,
      MatButtonModule,
      MatIconModule,
      MatTooltipModule,
      TranslateModule,
      NumberOnlyDirective,
      ErrorFormComponent,
      VerifyIdentityComponent,
      NgOtpInputModule,
      AutoRegistrationTopBarComponent
    ],
  templateUrl: './reset-password2.component.html',
  styleUrl: './reset-password2.component.scss'
})
export class ResetPassword2Component extends ComponentBase implements OnInit {
  @ViewChild('otpInputRef') otpInputRef: any;

  form!: FormGroup;

  /** Current step in the reset password flow */
  step: ResetPasswordStep = 'enter_phone';

  /** Mobile number submitted in step 1 (used in OTP and update steps) */
  submittedMobileNumber: string = '';

  /** OTP code entered by user (step 2) */
  otpCode: string = '';

  /** OTP code after verification success (sent with updatePassword) */
  verifiedCode: string = '';

  /** Loading state for OTP Confirm button */
  confirmLoading: boolean = false;

  /** Hide/show new password in step 3 */
  hidePassword: boolean = true;

  /** Selected verify method (SMS or Whatsapp) for forget password request */
  selectedVerifyType: VerifyMobileNumberType | null = null;

  otpInputConfig: NgOtpInputConfig = { length: 4 };

  get mobileNumber(): string {
    return this.form?.get('MobileNumber')?.value ?? '';
  }

  /** App domain for redirect after success */
  get appDomain(): string {
    return ((window as any).environment || environment).AppDomain;
  }

  /** Translation key for OTP step message: WhatsApp or SMS based on selected verify method */
  get otpStepMessageKey(): string {
    return this.selectedVerifyType === VerifyMobileNumberType.Whatsapp
      ? 'Enter_The_Code_WhatsApp'
      : 'Enter_The_Code_Phone';
  }

  /** Current UI direction (ltr/rtl) from locale */
  dir: string = localStorage.getItem('lang') || '';

  constructor(
    private readonly sharedService: SharedService,
    private readonly autoRegistrationService: AutoRegistrationService,
    @Inject(CONFIG) public config: AutoRegistrationConfig,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly translateService: TranslateService,
    private readonly dialog: MatDialog
  ) {
    super();
    translateService.onLangChange.subscribe((_) => {
      this.dir = _.lang;
    });
  }

  ngOnInit(): void {
    this.initForm();
    this.applyPendingPasswordFromQuery();
  }

  /** After verify (legacy /verify flow): jump to new-password step with pre-verified code */
  private applyPendingPasswordFromQuery(): void {
    const q = this.route.snapshot.queryParamMap;
    if (q.get('pendingPassword') !== '1' || !q.get('data')) {
      return;
    }
    try {
      const parsed = JSON.parse(q.get('data')!);
      const userName = parsed?.UserName as string | undefined;
      const code = parsed?.Code as string | undefined;
      if (!userName || !code) {
        return;
      }
      this.submittedMobileNumber = userName;
      this.verifiedCode = code;
      this.form.patchValue({ MobileNumber: userName });
      if (!this.form.contains('newPassword')) {
        this.form.addControl(
          'newPassword',
          new UntypedFormControl(null, [
            Validators.required,
            Validators.minLength(6),
            Validators.maxLength(20),
            Validators.pattern(/^(?=.*[A-Za-z])(?=.*\d).+$/),
          ])
        );
      }
      this.step = 'enter_new_password';
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        replaceUrl: true,
      });
    } catch {
      /* invalid JSON */
    }
  }

  initForm(): void {
    this.form = new UntypedFormGroup({
      MobileNumber: new UntypedFormControl(null, [
        Validators.required,
        Validators.maxLength(12),
        Validators.minLength(4),
      ]),
    });
  }

  /** Step 1: submit forgetPassword; on success go to OTP step (no navigation) */
  onSubmit(): void {
    if (this.form.invalid || !this.selectedVerifyType) return;
    const userName = this.form.get('MobileNumber')?.value?.trim() ?? '';

    this.loading = true;
    this.autoRegistrationService
      .forgetPassword({
        UserName: userName,
        VerifyMobileNumberType: this.selectedVerifyType!,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loading = false;
          this.checkResponse = this.sharedService.checkResponseStatus(
            res.Message,
            res.State
          );
          if (this.checkResponse) {
            this.sharedService.playSound('assets/sounds/done.mp3');
            this.submittedMobileNumber = userName;
            this.step = 'enter_otp';
            if (!this.form.contains('newPassword')) {
              this.form.addControl(
                'newPassword',
                new UntypedFormControl(null, [
                  Validators.required,
                  Validators.minLength(6),
                  Validators.maxLength(20),
                  Validators.pattern(/^(?=.*[A-Za-z])(?=.*\d).+$/),
                ])
              );
            }
          }
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  onOtpInputChange(otpCode: string): void {
    this.otpCode = otpCode;
  }

  /** Step 2: confirm OTP → call verification; on success go to new password step */
  onConfirmOtp(): void {
    if (!this.otpCode || this.otpCode.length !== 4 || this.confirmLoading) return;

    this.confirmLoading = true;
    this.autoRegistrationService
      .verification({
        UserName: this.submittedMobileNumber,
        Code: this.otpCode,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.confirmLoading = false))
      )
      .subscribe({
        next: (res) => {
          this.checkResponse = this.sharedService.checkResponseStatus(
            res.Message,
            res.State
          );
          if (this.checkResponse) {
            this.sharedService.playSound('assets/sounds/done.mp3');
            this.verifiedCode = this.otpCode;
            this.step = 'enter_new_password';
          }
        },
      });
  }

  /** Step 3: submit new password → updatePassword; on success redirect to login */
  onSubmitNewPassword(): void {
    const newPassword = this.form.get('newPassword')?.value?.trim();
    if (!newPassword || this.loading) return;

    this.loading = true;
    this.autoRegistrationService
      .updatePassword({
        UserName: this.submittedMobileNumber,
        Code: this.verifiedCode,
        newPassword,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loading = false;
          this.checkResponse = this.sharedService.checkResponseStatus(
            res.Message,
            res.State
          );
          if (this.checkResponse) {
            this.sharedService.playSound('assets/sounds/done.mp3');
            window.location.href = 'https://' + this.appDomain + '/login';
          }
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  onVerifyTypeChange(payload: { verifyType: VerifyMobileNumberType }): void {
    this.selectedVerifyType = payload?.verifyType ?? null;
  }

  /** Opens the video modal in a dialog */
  onOpenVideo(): void {
    this.dialog.open(VideoComponent, {
      minWidth: '60%',
      maxWidth: '100%',
      panelClass: 'aspect_ratio',
    });
  }

  backToPhoneNumberStep(): void {
    if (this.submittedMobileNumber) {
      this.form.patchValue({ MobileNumber: this.submittedMobileNumber });
    }
    if (this.form.contains('newPassword')) {
      this.form.removeControl('newPassword');
    }
    this.step = 'enter_phone';
  }
  
  backToOTPStep(): void {
    this.verifiedCode = '';
    this.step = 'enter_otp';
    setTimeout(() => {
      if (this.otpCode?.length === 4) {
        this.otpInputRef?.setValue(this.otpCode);
      }
    }, 0);
  }
}
